import { useSEO } from '../../hooks/useSEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yourmindshift24.com';

const SoFunktioniertEsPage = () => {
  useSEO({
    title: 'So funktioniert YourMindShift24 – Dein KI-Coach Schritt für Schritt erklärt',
    description: 'Erfahre, wie YourMindShift24 funktioniert: Onboarding, tägliche Check-ins, KI-Analyse und persönliche Begleitung für Klarheit, Fokus und mentale Stärke.',
    keywords: 'YourMindShift24 Funktionsweise, KI-Coach Onboarding, Check-ins erklärt, mentale Stärke App, Reflexion KI',
    canonical: '/so-funktioniert-es',
    ogTitle: 'So funktioniert YourMindShift24 – KI-Coach erklärt',
    ogDescription: 'Schritt für Schritt: Wie YourMindShift24 dir mit täglichen Check-ins, KI-Analyse und persönlichen Empfehlungen zu mehr Klarheit verhilft.',
    ogImage: `${SITE_URL}/og-image.jpg`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'So nutzt du YourMindShift24 – Schritt für Schritt',
      description: 'Lerne, wie du YourMindShift24 für tägliche Reflexion, Zielverfolgung und mentale Stärke nutzt.',
      totalTime: 'PT5M',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Onboarding',
          text: 'Beantworte einige einfache Fragen zu deinen Zielen und Herausforderungen, damit die KI ein persönliches Profil erstellt.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Check-ins durchführen',
          text: 'Starte täglich einen Morgen-, Mittag- oder Abend-Check-in, um deine Gedanken und Gefühle zu reflektieren.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'KI-Analyse & Zusammenfassungen erhalten',
          text: 'Die KI wertet deine Antworten aus und liefert dir tägliche oder wöchentliche Insights für persönliches Wachstum.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Fortschritt verfolgen',
          text: 'Verfolge deine Ziele, Streaks und Muster über Zeit und optimiere deine Routinen mit personalisierten Empfehlungen.',
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-20">
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            So funktioniert <span className="text-teal-600">Mindshift AI</span>
          </h1>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Dein persönlicher Begleiter für Klarheit, Fokus und mentale Stärke – direkt hier in Telegram.
            <strong className="text-teal-600"> Alle Daten werden sicher, anonym und vertraulich behandelt.</strong>
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              ✨ So funktioniert Mindshift AI:
            </h2>
          </div>

          {/* Steps */}
          <div className="space-y-12">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🧭</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    1. Onboarding
                  </h3>
                  <div className="space-y-3 text-gray-700 leading-relaxed">
                    <p>
                      Zu Beginn stellt dir Mindshift ein paar einfache Fragen, um deine wichtigsten Ziele, 
                      Herausforderungen und Themen zu verstehen.
                    </p>
                    <p>
                      Daraus entsteht ein persönliches Profil, das die KI in allen späteren Antworten berücksichtigt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">💬</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    2. Check-ins durchführen
                  </h3>
                  <div className="space-y-3 text-gray-700 leading-relaxed">
                    <p>
                      Du kannst jederzeit den Befehl <code className="bg-gray-100 px-2 py-1 rounded text-teal-600 font-mono">/checkin</code> verwenden, 
                      um einen kurzen Morgen-, Mittag- oder Abend-Check-in zu starten.
                    </p>
                    <p>
                      Die Fragen sind kurz, reflektierend und individuell auf dich abgestimmt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🤖</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    3. KI-Analyse & Zusammenfassungen
                  </h3>
                  <div className="space-y-3 text-gray-700 leading-relaxed">
                    <p>
                      Deine Antworten werden automatisch ausgewertet und in Tages- oder Wochensummaries 
                      zusammengefasst – klar, strukturiert und hilfreich für dein persönliches Wachstum.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📁</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    4. Alles wird gespeichert
                  </h3>
                  <div className="space-y-3 text-gray-700 leading-relaxed">
                    <p>
                      Deine Check-ins, Ziele und Themen werden sicher gespeichert, sodass du deinen 
                      Fortschritt jederzeit nachverfolgen kannst.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="mt-16 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-2xl mr-3">💡</span>
              Wichtig:
            </h3>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <div className="flex items-start space-x-3">
                <span className="text-teal-600 font-bold mt-1">•</span>
                <p>Du entscheidest selbst, wie oft du eincheckst – flexibel und ohne Druck.</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-teal-600 font-bold mt-1">•</span>
                <p>Du kannst Mindshift jederzeit etwas fragen oder um Unterstützung bitten.</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-teal-600 font-bold mt-1">•</span>
                <p>Die KI nutzt deine persönlichen Themen, um dir relevantere Antworten zu geben.</p>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 p-8 rounded-2xl mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              💡 Wichtig: Deine Privatsphäre steht an erster Stelle
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p className="text-gray-700 flex items-start">
                  <i className="ri-shield-check-line text-teal-500 mr-3 mt-1"></i>
                  Du entscheidest selbst, wie oft du eincheckst – flexibel und ohne Druck
                </p>
                <p className="text-gray-700 flex items-start">
                  <i className="ri-question-answer-line text-teal-500 mr-3 mt-1"></i>
                  Du kannst Mindshift jederzeit etwas fragen oder um Unterstützung bitten
                </p>
              </div>
              <div className="space-y-3">
                <p className="text-gray-700 flex items-start">
                  <i className="ri-user-forbid-line text-teal-500 mr-3 mt-1"></i>
                  <strong>100% anonym</strong> – keine persönlichen Daten erforderlich
                </p>
                <p className="text-gray-700 flex items-start">
                  <i className="ri-lock-line text-teal-500 mr-3 mt-1"></i>
                  <strong>Sicher verschlüsselt</strong> – alle Gespräche bleiben vertraulich
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Bereit für deinen persönlichen KI-Coach?
              </h3>
              <p className="text-gray-700 mb-6">
                Starte noch heute mit Mindshift AI und erlebe, wie einfach persönliches Wachstum sein kann.
              </p>
              <a
                href="https://t.me/mindshift_ai_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-telegram-line text-xl mr-3"></i>
                Jetzt 7 Tage kostenlos testen
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoFunktioniertEsPage;
